/**
 * Teto de qualidade do vídeo da chamada — por que existe e como foi escolhido.
 *
 * **O relato (07/08/2026).** O Augusto, depois de testar notebook × celular:
 * *"as câmeras têm uma qualidade muito grande … a qualidade ficou alta demais e
 * pesou muito na chamada, os movimentos ficaram um pouco lentos. Seria
 * interessante diminuir um pouco a qualidade, principalmente na frontal e
 * também na traseira dos dispositivos móveis … e também no desktop, porque às
 * vezes a webcam da pessoa tem uma qualidade absurda de 1080p e vai pesar."*
 *
 * ## Por que capar a CAPTURA, e não confiar no simulcast
 *
 * O LiveKit já publica em camadas (simulcast) e o servidor entrega a cada
 * espectador a que couber na banda dele. Isso resolve o lado de QUEM RECEBE.
 *
 * Não resolve o lado de QUEM ENVIA: capturar 1080p e codificar três camadas a
 * partir disso é trabalho do processador do próprio aparelho, feito antes de
 * qualquer byte sair. É esse custo que aquece o celular e engasga o movimento —
 * e é por isso que a correção é limitar a captura, não a entrega.
 *
 * Num celular a conta é pior ainda: a câmera entrega 4K nativo, o navegador
 * reamostra para o que pedirmos, e cada pixel a mais nesse caminho é bateria e
 * latência que nunca viram utilidade numa janela de 320px.
 *
 * ## Os números
 *
 * - **Celular → 360p, 20 fps.** Numa tela de telefone o interlocutor aparece
 *   em poucos centímetros; 360p já satura o que o olho distingue ali, e o
 *   custo de codificação cai perto de um quarto do de 720p.
 * - **Desktop → 540p, 24 fps.** Meio-termo: janela maior pede mais definição,
 *   mas 1080p de webcam é desperdício que ninguém percebe e todos pagam.
 * - **Compartilhamento de tela → alto de propósito.** Aqui a resolução não é
 *   estética, é legibilidade: texto de planilha ou de sistema borrado torna o
 *   compartilhamento inútil. É o único caso em que apertar seria errado.
 */
import { VideoPresets, type VideoCaptureOptions, type TrackPublishDefaults } from "livekit-client";

/** Perfil de qualidade escolhido para o aparelho atual. */
export interface PerfilDeQualidade {
  captura: VideoCaptureOptions;
  publicacao: TrackPublishDefaults;
  /** Rótulo curto — usado em log e teste, não na interface. */
  nome: "celular" | "desktop";
}

/**
 * O aparelho é de toque e tela pequena?
 *
 * Recebe os sinais por parâmetro em vez de ler `window` direto para poder ser
 * testado sem simular um navegador inteiro.
 *
 * Usa DOIS sinais porque cada um sozinho erra: `pointer: coarse` também casa
 * com notebook de tela sensível ao toque (que tem processador de sobra), e
 * largura pequena sozinha casa com uma janela de desktop encolhida (idem). A
 * combinação erra bem menos, e o custo do erro é só um perfil de qualidade
 * ligeiramente conservador.
 */
export function ehAparelhoMovel(opcoes: {
  ponteiroGrosso: boolean;
  larguraDaTela: number;
}): boolean {
  return opcoes.ponteiroGrosso && opcoes.larguraDaTela <= 900;
}

/** Lê os sinais do navegador. Fora de navegador, assume desktop. */
export function detectarAparelhoMovel(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return ehAparelhoMovel({
    ponteiroGrosso: window.matchMedia("(pointer: coarse)").matches,
    larguraDaTela: window.screen?.width ?? window.innerWidth ?? 1920,
  });
}

/**
 * O perfil de captura e publicação para este aparelho.
 *
 * `facingMode` NÃO é definido aqui: quem escolhe frontal ou traseira é o
 * usuário, pelo botão de inverter câmera, e fixá-lo neste padrão sobrescreveria
 * a escolha dele a cada republicação da faixa.
 */
export function perfilDeQualidade(movel = detectarAparelhoMovel()): PerfilDeQualidade {
  if (movel) {
    return {
      nome: "celular",
      captura: {
        resolution: VideoPresets.h360.resolution,
      },
      publicacao: {
        videoEncoding: {
          maxBitrate: 500_000,
          maxFramerate: 20,
        },
        // Simulcast segue LIGADO: capar a captura resolve o custo de quem
        // envia; as camadas resolvem o de quem recebe. São problemas
        // diferentes, e desligar um para arrumar o outro pioraria os dois.
        simulcast: true,
        screenShareEncoding: ENCODING_DA_TELA,
      },
    };
  }

  return {
    nome: "desktop",
    captura: {
      resolution: VideoPresets.h540.resolution,
    },
    publicacao: {
      videoEncoding: {
        maxBitrate: 900_000,
        maxFramerate: 24,
      },
      simulcast: true,
      screenShareEncoding: ENCODING_DA_TELA,
    },
  };
}

/**
 * Compartilhamento de tela — deliberadamente ALTO.
 *
 * É a exceção da regra deste arquivo. Texto de planilha ou de sistema em 360p
 * fica ilegível, e um compartilhamento ilegível não serve para nada; o custo
 * extra é aceitável porque compartilhar tela é episódico, não o estado normal
 * da chamada. `maxFramerate` baixo porque tela é conteúdo quase parado — os 15
 * quadros vão para nitidez em vez de fluidez.
 */
const ENCODING_DA_TELA = {
  maxBitrate: 1_500_000,
  maxFramerate: 15,
} as const;

/** A câmera oposta à atual. Frontal ⇄ traseira. */
export function inverterCamera(atual: "user" | "environment"): "user" | "environment" {
  return atual === "user" ? "environment" : "user";
}
